import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import { supabase } from '../../src/lib/supabase';
import { resolverAvisosPendentes, AvisoParaResolucao, ConfirmacaoAviso } from '../../src/lib/resolverAvisosPendentes';
import AvisoModal, { AvisoModalAviso } from './AvisoModal';
import AceiteBoletoUnicoModal from './AceiteBoletoUnicoModal';
import { useConviteBoletoUnico } from '../../src/hooks/useConviteBoletoUnico';
import { deveAbrirConviteAutomatico } from '../../src/lib/boletoUnico/aceitePortal';
import { Loader2 } from 'lucide-react';
import { useMarcaPortal } from './marca/MarcaPortalContext';
import ComunicadoMarcaModal from './marca/ComunicadoMarcaModal';
import { CATEGORIA_COMUNICADO, comunicadoParaAbrir } from './marca/marcaPortal';

// Ponto único de gate para as rotas internas do Portal do Consumidor (depois do
// login e da troca de senha obrigatória): sessão válida, role, senha provisória e
// avisos pendentes. Usado como rota de layout (<Outlet/>) em App.tsx, envolvendo
// /inicio, /perfil, /faturas, /parcelamentos e /ajuda — substitui a checagem que
// antes era copiada em cada página (e que faltava em PortalConsumidorPerfil.tsx).
// Centralizado aqui também porque um aviso bloqueante não pode depender de qual
// rota do portal o consumidor abriu primeiro.
//
// Falha de rede ao buscar avisos: mostra um aviso e libera o acesso (fail-open) —
// nunca trava um cliente pagante fora do portal por instabilidade de conexão.

interface AvisoRow extends AvisoParaResolucao, AvisoModalAviso {}

const PortalConsumidorGate = () => {
    const { user, profile, loading } = useAuth();
    const { addToast } = useToast();
    // Marca do portal (#6117): espera ela antes de desenhar, senão o cliente da Gran vê a
    // tela da Vigor piscar; e o "Sair" volta para a tela de entrada da marca dele.
    const { marca, carregando: carregandoMarca } = useMarcaPortal();
    const [avisos, setAvisos] = useState<AvisoRow[]>([]);
    const [confirmacoes, setConfirmacoes] = useState<ConfirmacaoAviso[]>([]);
    const [carregandoAvisos, setCarregandoAvisos] = useState(true);
    const [dispensados, setDispensados] = useState<Set<string>>(new Set());
    const totalInicial = useRef<number | null>(null);

    const [comunicadoAberto, setComunicadoAberto] = useState<AvisoRow | null>(null);
    const comunicadoJaAbriu = useRef(false);
    // marcarVisualizado é declarada depois dos returns antecipados; o efeito do comunicado
    // (declarado antes deles, como todo hook) chama pela ref.
    const marcarVisualizadoRef = useRef<((avisoId: string) => void) | null>(null);

    const [senhaProvisoriaPendente, setSenhaProvisoriaPendente] = useState(false);
    const [carregandoConsumidor, setCarregandoConsumidor] = useState(true);

    // Convite do boleto único (#5575). Aviso da categoria 'aceite' NÃO passa pela fila genérica
    // abaixo: o AvisoModal só grava "ciência" por login, e a adesão é por UC. Ele tem modal
    // próprio, que abre sozinho uma única vez; depois o convite fica no cartão do Início.
    const convite = useConviteBoletoUnico();
    const [conviteAberto, setConviteAberto] = useState(false);
    const conviteJaAbriu = useRef(false);

    useEffect(() => {
        if (!user || profile?.role !== 'consumidor') { setCarregandoConsumidor(false); return; }
        let cancelado = false;
        (async () => {
            setCarregandoConsumidor(true);
            try {
                const { data, error } = await supabase
                    .from('consumidores')
                    .select('senha_provisoria_pendente, ultimo_acesso_em')
                    .eq('id', user.id)
                    .maybeSingle();
                if (error) throw error;
                // Fail-open: sem linha em consumidores (conta antiga, criada antes
                // dessa tabela existir) não bloqueia — mesma regra do resto do gate.
                if (!cancelado) setSenhaProvisoriaPendente(data?.senha_provisoria_pendente === true);

                // Carimba o acesso. É aqui e não no handleLogin porque este gate é o
                // único ponto por onde TODA sessão do portal passa — inclusive a que
                // volta com token já salvo, sem passar pela tela de login de novo.
                // Roda antes do redirect pra /nova-senha, então quem loga e desiste na
                // troca de senha também conta como "entrou". Falha aqui não interessa
                // ao consumidor (é métrica interna): só loga e segue.
                if (data) {
                    supabase
                        .from('consumidores')
                        .update({ ultimo_acesso_em: new Date().toISOString() })
                        .eq('id', user.id)
                        .then(({ error: errAcesso }) => {
                            if (errAcesso) console.error('Erro ao registrar acesso do consumidor:', errAcesso);
                        });
                }
            } catch (err) {
                console.error('Erro ao verificar senha provisória:', err);
                if (!cancelado) setSenhaProvisoriaPendente(false);
            } finally {
                if (!cancelado) setCarregandoConsumidor(false);
            }
        })();
        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, profile]);

    const sessaoValida = !!user && (!profile || profile.role === 'consumidor') && !senhaProvisoriaPendente;

    useEffect(() => {
        if (!user || profile?.role !== 'consumidor' || senhaProvisoriaPendente) return;

        let cancelado = false;
        (async () => {
            setCarregandoAvisos(true);
            try {
                const [{ data: avisosData, error: errAvisos }, { data: confirmData, error: errConfirm }] = await Promise.all([
                    supabase.from('avisos_consumidor').select('id, titulo, mensagem, categoria, bloqueante, url_anexo, data_inicio, data_fim'),
                    supabase.from('avisos_consumidor_confirmacoes').select('aviso_id, visualizado_em, confirmado_em').eq('profile_id', user.id),
                ]);
                if (errAvisos) throw errAvisos;
                if (errConfirm) throw errConfirm;
                if (cancelado) return;
                setAvisos((avisosData || []) as AvisoRow[]);
                setConfirmacoes((confirmData || []) as ConfirmacaoAviso[]);
            } catch (err) {
                console.error('Erro ao carregar avisos pendentes do portal:', err);
                // Fail-open: nunca trava um cliente pagante fora do portal por
                // instabilidade de conexão — só avisa e segue liberando o acesso.
                // Usa o mesmo componente de toast do resto do sistema (useToast),
                // não um banner ad-hoc.
                if (!cancelado) addToast('Não foi possível verificar avisos no momento. Tente novamente mais tarde.', 'error');
            } finally {
                if (!cancelado) setCarregandoAvisos(false);
            }
        })();
        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, profile, senhaProvisoriaPendente]);

    const pendentes = useMemo(() => {
        // 'aceite' tem modal próprio (convite do boleto único) e 'comunicado' também (#6117:
        // informativo, sem "li e aceito" — ver ComunicadoMarcaModal).
        const genericos = avisos.filter((a) => a.categoria !== 'aceite' && a.categoria !== CATEGORIA_COMUNICADO);
        const todos = resolverAvisosPendentes(genericos, confirmacoes, new Date());
        return todos.filter((p) => p.aviso.bloqueante || !dispensados.has(p.aviso.id));
    }, [avisos, confirmacoes, dispensados]);

    useEffect(() => {
        if (totalInicial.current === null && !carregandoAvisos) {
            totalInicial.current = pendentes.length;
        }
    }, [carregandoAvisos, pendentes.length]);

    // Abre o convite depois dos avisos obrigatórios, nunca empilhado sobre eles. Uma vez por
    // montagem, e só se o cliente nunca o viu: marcar como visto na abertura é o que impede o
    // convite de voltar a cada login.
    useEffect(() => {
        if (conviteJaAbriu.current || carregandoAvisos || convite.carregando || pendentes.length > 0) return;
        // Cliente de parceira já é 100% boleto único: não há o que aderir.
        if (!marca.mostrarConviteBoletoUnico) return;
        if (!convite.aviso || !convite.convite) return;
        if (!deveAbrirConviteAutomatico({
            avisoPublicado: true,
            jaVisualizado: convite.jaVisualizado,
            podeAderir: convite.convite.podeAderir,
        })) return;
        conviteJaAbriu.current = true;
        setConviteAberto(true);
        convite.marcarVisualizado();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carregandoAvisos, convite.carregando, convite.aviso, convite.convite, convite.jaVisualizado, pendentes.length, marca.mostrarConviteBoletoUnico]);

    // Comunicado da marca: abre sozinho uma única vez, depois dos avisos obrigatórios e nunca
    // empilhado sobre eles. A abertura já grava "visualizado", então não volta nem se o
    // cliente fechar a aba sem clicar em "Entendi".
    useEffect(() => {
        if (comunicadoJaAbriu.current || carregandoAvisos || pendentes.length > 0 || conviteAberto) return;
        const c = comunicadoParaAbrir(avisos, confirmacoes, new Date());
        if (!c) return;
        comunicadoJaAbriu.current = true;
        setComunicadoAberto(c);
        marcarVisualizadoRef.current?.(c.id);
    }, [carregandoAvisos, pendentes.length, conviteAberto, avisos, confirmacoes]);

    if (loading || carregandoConsumidor || (!!user && carregandoMarca)) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader2 size={32} className="animate-spin text-primary" />
            </div>
        );
    }

    if (!user) return <Navigate to={marca.rotaLogin} replace />;
    if (profile && profile.role !== 'consumidor') return <Navigate to="/" replace />;
    if (senhaProvisoriaPendente) return <Navigate to="/portal-consumidor/nova-senha" replace />;
    if (!sessaoValida) return null;

    const marcarVisualizado = async (avisoId: string) => {
        if (confirmacoes.some((c) => c.aviso_id === avisoId)) return;
        const agora = new Date().toISOString();
        const { error } = await supabase
            .from('avisos_consumidor_confirmacoes')
            .insert({ aviso_id: avisoId, profile_id: user!.id, visualizado_em: agora });
        if (!error) {
            setConfirmacoes((prev) => [...prev, { aviso_id: avisoId, visualizado_em: agora, confirmado_em: null }]);
        } else {
            // Não bloqueia nem avisa o consumidor por isso — é só o registro de "visto",
            // que confirmarAviso (abaixo) já sabe recriar via upsert se essa linha nunca
            // tiver existido. Fica só o log para investigação.
            console.error('Erro ao marcar aviso como visualizado:', error);
        }
    };

    marcarVisualizadoRef.current = marcarVisualizado;

    const confirmarAviso = async (avisoId: string) => {
        const agora = new Date().toISOString();
        // upsert (não update): se o INSERT de marcarVisualizado tiver falhado por
        // qualquer motivo (rede, RLS), um UPDATE aqui não acharia nenhuma linha e
        // devolveria sucesso (0 linhas afetadas não é erro) — o consumidor clicaria
        // "Li e aceito", veria "Confirmando..." e nada aconteceria, sem nenhum aviso,
        // preso num aviso bloqueante indefinidamente. upsert garante que a linha passa
        // a existir com confirmado_em preenchido de qualquer forma. Não inclui
        // visualizado_em no payload de propósito: o trigger protege_avisos_confirmacao
        // barra reescrever visualizado_em depois de definido, e por não estar no
        // payload o Postgres mantém o valor antigo no caminho normal (ON CONFLICT).
        const { error } = await supabase
            .from('avisos_consumidor_confirmacoes')
            .upsert(
                { aviso_id: avisoId, profile_id: user!.id, confirmado_em: agora },
                { onConflict: 'aviso_id,profile_id' }
            );
        if (!error) {
            setConfirmacoes((prev) => {
                const jaExiste = prev.some((c) => c.aviso_id === avisoId);
                if (!jaExiste) return [...prev, { aviso_id: avisoId, visualizado_em: agora, confirmado_em: agora }];
                return prev.map((c) => (c.aviso_id === avisoId ? { ...c, confirmado_em: agora } : c));
            });
        } else {
            console.error('Erro ao confirmar aviso:', error);
            addToast('Não foi possível registrar sua confirmação agora. Tente novamente.', 'error');
        }
    };

    const avisoAtual = pendentes[0];
    const posicao = totalInicial.current !== null ? totalInicial.current - pendentes.length + 1 : 1;

    return (
        <>
            <Outlet />

            {!carregandoAvisos && avisoAtual && (
                <AvisoModal
                    aviso={avisoAtual.aviso}
                    posicao={posicao}
                    total={totalInicial.current ?? pendentes.length}
                    onVisualizado={() => marcarVisualizado(avisoAtual.aviso.id)}
                    onConfirmar={() => confirmarAviso(avisoAtual.aviso.id)}
                    onFechar={avisoAtual.aviso.bloqueante ? undefined : () => setDispensados((prev) => new Set(prev).add(avisoAtual.aviso.id))}
                />
            )}

            {comunicadoAberto && (
                <ComunicadoMarcaModal
                    comunicado={comunicadoAberto}
                    onFechar={() => {
                        const id = comunicadoAberto.id;
                        setComunicadoAberto(null);
                        confirmarAviso(id);
                    }}
                />
            )}

            {/* Fica aberto até o cliente fechar, mesmo que o convite recarregue por trás depois
                do aceite (a lista de UCs muda e o modal não pode sumir antes da confirmação). */}
            {conviteAberto && convite.aviso && convite.convite && (
                <AceiteBoletoUnicoModal
                    aviso={convite.aviso}
                    linhas={convite.convite.linhas}
                    onAceitar={convite.aceitar}
                    onFechar={() => setConviteAberto(false)}
                />
            )}
        </>
    );
};

export default PortalConsumidorGate;
